import { Button as MuiButton, type ButtonProps as MuiButtonProps, styled } from '@mui/material';
import { type ComponentProps, type FC, forwardRef } from 'react';

import { preventForwardProps } from '../../../../utilities';
import { ButtonMaybeTooltip, type ButtonTooltipProps } from '../ButtonMaybeTooltip';

const StyledButton = styled(
  MuiButton,
  preventForwardProps(['buttonSize', 'rounded'])
)<{ buttonSize: 'xs' | 's' | 'm'; rounded: boolean }>(({ theme, buttonSize, rounded }) => {
  const height = buttonSize === 'xs' ? 24 : buttonSize === 's' ? 36 : 48;
  const borderWidth = buttonSize === 'm' ? 2 : 1;

  return {
    ...theme.typography[buttonSize === 'm' ? 'button-m' : buttonSize === 's' ? 'button-s' : 'button-xs'],
    padding:
      buttonSize === 'm' ? theme.spacing(1.5, 3) : buttonSize === 's' ? theme.spacing(0.5, 1.5) : theme.spacing(0.5, 1),
    minHeight: height,
    borderRadius: rounded ? height / 2 : theme.spacing(1),

    '&, &:hover, &.Mui-disabled': {
      // It is important for a button to have a border in order to be perceptible
      // when running in Windows High-Contrast Mode (WHCM).
      border: `${borderWidth}px solid transparent`,
    },

    '&:focus-visible': theme.mixins.focusRing,

    '.MuiButton-startIcon': {
      marginRight: rounded ? theme.spacing(0.5) : undefined,
    },
  };
});

export type BaseButtonProps = Omit<MuiButtonProps, 'size' | 'type'> & {
  component?: string | FC | unknown;
  htmlFor?: string;
  target?: '_blank';
  download?: ComponentProps<'a'>['download'];
  // This component applies `type="button"` by default which, while being a good
  // default for buttons, yields invalid HTML when used to render links. Passing
  // `type={undefined}` wouldn’t work since it would cause the default value to
  // be applied, which is why we need to accept `null` as a value, and skip
  // rendering the `type` attribute if set as such.
  type?: MuiButtonProps['type'] | null;
  size?: 'xs' | 's' | 'm';
  rounded?: boolean;
} & ButtonTooltipProps;

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(function BaseButton(
  { type = 'button', size = 'm', rounded = false, tooltip, tooltipProps, ...rest },
  ref
) {
  const button = (
    <StyledButton
      disableRipple
      variant="contained"
      type={type ?? undefined}
      buttonSize={size}
      rounded={rounded}
      {...rest}
      ref={ref}
    />
  );

  if (tooltip) {
    return (
      <ButtonMaybeTooltip tooltip={tooltip} disabled={rest.disabled} tooltipProps={tooltipProps}>
        {button}
      </ButtonMaybeTooltip>
    );
  }

  return button;
});
